import omit from 'lodash.omit'
import { makeAutoObservable, toJS } from 'mobx'
import { CreateViewInput, ViewColumnTemplateFragment, ViewFragment, ViewType } from 'types/graphql'

import { toViewColumnInput } from 'src/components/Reports/columnInput'
import { TableViewColumnTemplateStore } from 'src/models/TableViews/TableViewColumnTemplateStore'

import { TableView } from './TableView'

export type TableViewManagerSerialized = {
  searchQuery?: string
  selectedViewId: number | undefined
  views: ViewFragment[]
}

export type EntityLayerBrowseKind = 'address' | 'parcel'

export class TableViewManager {
  public readonly entityLayerBrowseKind?: EntityLayerBrowseKind
  public readonly entityLayerId?: number
  public readonly recordTypeId?: number
  public readonly viewDashboardId?: number
  public readonly viewType: ViewType

  private _columnTemplateStore: TableViewColumnTemplateStore
  private _views: TableView[] = []
  public selectedView: TableView | undefined

  constructor(args: {
    entityLayerBrowseKind?: EntityLayerBrowseKind
    entityLayerId?: number
    recordTypeId?: number
    viewDashboardId?: number
    viewType: ViewType
  }) {
    this.entityLayerBrowseKind = args.entityLayerBrowseKind
    this.entityLayerId = args.entityLayerId
    this.recordTypeId = args.recordTypeId
    this.viewDashboardId = args.viewDashboardId
    this.viewType = args.viewType
    this._columnTemplateStore = new TableViewColumnTemplateStore({
      columnTemplates: [],
    })
    makeAutoObservable(this)
  }

  public initializeViews(args: {
    columnTemplates: ViewColumnTemplateFragment[]
    views: ViewFragment[]
    hideBaseFilters?: boolean
  }) {
    this._columnTemplateStore = new TableViewColumnTemplateStore({
      columnTemplates: args.columnTemplates,
    })
    this._views = args.views
      .sort((a, b) => a.displayIndex - b.displayIndex)
      .map((v) =>
        TableView.fromProtocol(v, this._columnTemplateStore, {
          hideBaseFilters: args.hideBaseFilters,
        })
      )
    this.selectedView = this._views[0]
  }

  public get columnTemplates(): ViewColumnTemplateFragment[] {
    return this._columnTemplateStore.columnTemplates
  }

  public get columnTemplateStore(): TableViewColumnTemplateStore {
    return this._columnTemplateStore
  }

  public get views(): TableView[] {
    return this._views
  }

  public get viewsInDatabaseOrder(): TableView[] {
    return this._views.slice().sort((a, b) => a.databaseDisplayIndex - b.databaseDisplayIndex)
  }

  public getCreateViewInput(view: ViewFragment | undefined): CreateViewInput {
    return {
      ...omit(view, 'id', 'updatedAt', 'entityLayer', 'uuid'),
      entityLayerId: view?.entityLayer?.id,
      columns: (view?.columns ?? []).map(toViewColumnInput),
      recordTypeId: this.recordTypeId,
      viewDashboardId: this.viewDashboardId,
    }
  }

  public appendView(protocol: ViewFragment): TableView {
    const view = TableView.fromProtocol(protocol, this._columnTemplateStore)
    this._views.push(view)
    this._updateDisplayIndices()
    return view
  }

  public deleteView(index: number) {
    if (!this._isViewIndexValid(index)) {
      return
    }
    const viewToDelete = this._views[index]
    this._views.splice(index, 1)
    this._updateDisplayIndices()

    // Update the selected view if needed
    if (!this._views.length) {
      this.selectedView = undefined
    } else if (viewToDelete?.id === this.selectedView?.id) {
      this.selectedView = this._views[0]
    }
  }

  public duplicateView(index: number) {
    if (!this._isViewIndexValid(index)) {
      return
    }
    const sourceView = this._views[index]
    if (!sourceView) {
      return
    }
    const clone = sourceView.clone()
    clone.displayIndex = this._views.length
    this._views.push(clone)
  }

  public getViewById(viewId: number) {
    return this._getViewsById().get(viewId)
  }

  public getViewByUuid(viewUuid: string) {
    return this._views.find((view) => view.uuid === viewUuid)
  }

  public reorderViews(viewIds: number[]) {
    if (viewIds.length !== this._views.length) {
      return
    }

    const viewsById = this._getViewsById()
    const newViews: TableView[] = []
    for (let i = 0; i < viewIds.length; i++) {
      const viewId = viewIds[i]
      if (!viewId) {
        break
      }
      const view = viewsById.get(viewId)
      if (!view) {
        break
      }
      newViews.push(view)
    }
    if (newViews.length !== this._views.length) {
      return
    }

    this._views = newViews
    this._updateDisplayIndices()
  }

  public reset() {
    this._columnTemplateStore.reset()
    this._views = []
    this.selectedView = undefined
  }

  public saveViewsOrder() {
    this._views.forEach((v, index) => {
      v.databaseDisplayIndex = index
    })
  }

  public get serialized(): TableViewManagerSerialized {
    return {
      selectedViewId: this.selectedView?.id,
      views: this._views.map((view) => toJS(view.protocol)),
    }
  }

  public setSelectedViewId(viewId: number) {
    const view = this._views.find((v) => v.id === viewId)
    if (view) {
      this.selectedView = view
    }
  }

  public setSelectedViewUuid(viewUuid: string): boolean {
    const view = this.getViewByUuid(viewUuid)
    if (view) {
      this.selectedView = view
      return true
    }
    return false
  }

  private _getViewsById(): Map<number, TableView> {
    return new Map(this._views.map((v) => [v.id, v]))
  }

  private _isViewIndexValid(index: number) {
    return index >= 0 && index < this._views.length
  }

  private _updateDisplayIndices() {
    this._views.forEach((v, index) => {
      v.displayIndex = index
    })
  }
}
